import * as forms from "@client/forms";
import * as widgets from "@client/forms/widgets";
import * as router from "@client/router";
import * as analytics from "@client/shared/analytics";
import {Response, server} from "@reactivated";
import * as React from "react";

type ResponseField = {
    readonly kind: "select";
    readonly name: string;
    readonly label: string;
    readonly required: true;
    readonly options: readonly (readonly [Response, string])[];
};

const responseName = (index: number) => `response_${index}`;

export const CheckIn = router.CheckIn.withHooks((props) => {
    const checkIn = props.resolved.journal.check_in;

    if (checkIn == null) {
        throw new Error("Whoops");
    }

    const isReadOnly = checkIn.existing != null;

    const responses = forms.useForm(
        {
            fields: Object.fromEntries(
                checkIn.responses.map((response, index): [string, ResponseField] => [
                    responseName(index),
                    {
                        kind: "select",
                        name: responseName(index),
                        label: response.question.label,
                        required: true,
                        options: response.question.response_labels.map(
                            ({value, label}) => [value, label] as const,
                        ),
                    },
                ]),
            ),
        },
        {
            initial: Object.fromEntries(
                checkIn.responses.map((response, index) => [
                    responseName(index),
                    response.response,
                ]),
            ),
        },
    );

    const notes = forms.useField(
        {kind: "textarea", name: "notes"},
        {initial: checkIn.existing?.notes ?? ""},
    );

    return {
        checkIn,
        isReadOnly,
        responses,
        notes,
    };
})
    .withIndex((props) => {
        return (
            <div style={{display: "flex", flexDirection: "column", gap: 25}}>
                {props.checkIn.responses.map((_, index) => {
                    const field = props.responses.fields[responseName(index)];

                    return (
                        <forms.Label
                            key={index}
                            label={field.label}
                            error={field.errors?.[0] ?? null}
                        >
                            <widgets.QuickSelect
                                type="text"
                                name={field.name}
                                shrinkLast={false}
                                options={field.options.map(([value, label]) => ({
                                    value,
                                    label,
                                }))}
                                value={field.value}
                                readOnly={props.isReadOnly}
                                onChange={(value) => {
                                    const member = field.options.find(
                                        ([option]) => option === value,
                                    );
                                    if (member != null) {
                                        field.onChange(member[0]);
                                    }
                                }}
                            />
                        </forms.Label>
                    );
                })}
                <forms.Label label={props.checkIn.notes_label} error={null}>
                    <forms.Widget field={props.notes} readOnly={props.isReadOnly} />
                </forms.Label>
            </div>
        );
    })
    .withActions((props) => [
        props.checkIn.existing == null
            ? {
                  icon: "save",
                  title: "Save",
                  onClick: async () => {
                      const response = await forms.submitForm(props.responses, (data) =>
                          server.journal.day.check_in({
                              key: props.resolved.journal.day.key,
                              user_id: props.currentlyManagedUserId,
                              notes: props.notes.value,
                              responses: props.checkIn.responses.map(
                                  (responseData, index) => ({
                                      question: responseData.question.id,
                                      response: data[responseName(index)],
                                  }),
                              ),
                          }),
                      );

                      if (response?.type === "success") {
                          const eventProperties = Object.fromEntries(
                              props.checkIn.responses.map((responseData, index) => {
                                  return [
                                      `check_in_${responseData.question.type}`,
                                      props.responses.values[responseName(index)],
                                  ] as const;
                              }),
                          );

                          void analytics.track("CHECK_IN_SUBMITTED", eventProperties);
                          props.notify("Check-in submitted");
                          void router.push("journal.journal", {
                              key: props.resolved.journal.day.key,
                          });
                      }
                  },
              }
            : null,
    ])
    .withOptions(() => ({
        isDelegatable: true,
        title: `Check In`,
        back: "Journal",
        actionsStyle: "contrast",
    }));
